/**
 * Pure client-side filter logic for the custom Report Builder.
 * Rows are fetched via RLS-scoped Supabase queries, then filtered here.
 */

export type ColumnType = 'string' | 'number' | 'date' | 'boolean'

export type Operator =
  | 'equals'
  | 'contains'
  | 'gte'
  | 'lte'
  | 'time_gte'
  | 'time_lte'
  | 'in_list'
  | 'not_in_list'
  | 'is_true'
  | 'is_false'

export interface ColumnDef {
  key: string
  label: string
  type: ColumnType
}

export interface FilterRow {
  column: string
  operator: Operator
  value: string
}

/** Derived values (e.g. flags combining several raw columns) keyed by column key. */
export type ComputedFields = Record<string, (row: any) => any>

export const OPERATORS_BY_TYPE: Record<ColumnType, { value: Operator; label: string }[]> = {
  string: [
    { value: 'contains', label: 'contains' },
    { value: 'equals', label: 'equals' },
    { value: 'in_list', label: 'is one of' },
    { value: 'not_in_list', label: 'is not one of' },
  ],
  number: [
    { value: 'equals', label: 'equals' },
    { value: 'gte', label: '>=' },
    { value: 'lte', label: '<=' },
  ],
  date: [
    { value: 'gte', label: 'on/after' },
    { value: 'lte', label: 'on/before' },
    { value: 'time_gte', label: 'time at/after' },
    { value: 'time_lte', label: 'time at/before' },
  ],
  boolean: [
    { value: 'is_true', label: 'is true' },
    { value: 'is_false', label: 'is false' },
  ],
}

export function getByPath(obj: any, path: string, computed?: ComputedFields): any {
  if (computed && Object.prototype.hasOwnProperty.call(computed, path)) {
    return computed[path](obj)
  }
  if (path === 'employee.name' && obj?.employee) {
    return `${obj.employee.first_name || ''} ${obj.employee.last_name || ''}`.trim()
  }
  return path.split('.').reduce((acc, part) => (acc == null ? acc : acc[part]), obj)
}

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/

/** Local calendar day (YYYY-MM-DD) of a date-only string or timestamp. */
function localDayOf(value: any): string | null {
  if (value == null || value === '') return null
  const str = String(value)
  if (DATE_ONLY_RE.test(str)) return str
  const d = new Date(str)
  if (Number.isNaN(d.getTime())) return null
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${mm}-${dd}`
}

/** Minutes since local midnight for a timestamp, or null when unparseable. */
function minutesOfDay(value: any): number | null {
  if (value == null || value === '') return null
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return null
  return d.getHours() * 60 + d.getMinutes()
}

/** Parse "HH:MM" (24h) into minutes since midnight, or null. */
function parseTimeOfDay(value: string): number | null {
  const match = /^(\d{1,2}):(\d{2})/.exec(value.trim())
  if (!match) return null
  const minutes = Number(match[1]) * 60 + Number(match[2])
  return Number.isNaN(minutes) ? null : minutes
}

export function matchesFilter(
  row: any,
  filter: FilterRow,
  columns: ColumnDef[],
  computed?: ComputedFields
): boolean {
  if (!filter.column) return true
  const colDef = columns.find((c) => c.key === filter.column)
  if (!colDef) return true
  const cellValue = getByPath(row, filter.column, computed)

  if (colDef.type === 'boolean') {
    const boolVal = !!cellValue
    return filter.operator === 'is_true' ? boolVal : !boolVal
  }

  // Empty filter values are optional placeholders (e.g. preset worker filter left blank).
  if (filter.value === '') return true

  if (colDef.type === 'number') {
    const num = Number(cellValue)
    const target = Number(filter.value)
    if (Number.isNaN(num)) return false
    if (filter.operator === 'gte') return num >= target
    if (filter.operator === 'lte') return num <= target
    return num === target
  }

  if (colDef.type === 'date') {
    if (filter.operator === 'time_gte' || filter.operator === 'time_lte') {
      const target = parseTimeOfDay(filter.value)
      if (target == null) return true
      const minutes = minutesOfDay(cellValue)
      if (minutes == null) return false
      return filter.operator === 'time_gte' ? minutes >= target : minutes <= target
    }
    if (DATE_ONLY_RE.test(filter.value.trim())) {
      // Compare by local calendar day so "on/before" includes the whole end day.
      const cellDay = localDayOf(cellValue)
      if (!cellDay) return false
      const target = filter.value.trim()
      if (filter.operator === 'gte') return cellDay >= target
      if (filter.operator === 'lte') return cellDay <= target
      return cellDay === target
    }
    if (!cellValue) return false
    const d = new Date(cellValue).getTime()
    const target = new Date(filter.value).getTime()
    if (filter.operator === 'gte') return d >= target
    if (filter.operator === 'lte') return d <= target
    return d === target
  }

  const str = String(cellValue ?? '').toLowerCase().trim()
  const target = filter.value.toLowerCase()
  if (filter.operator === 'in_list' || filter.operator === 'not_in_list') {
    const values = target
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean)
    if (values.length === 0) return true
    const found = values.includes(str)
    return filter.operator === 'in_list' ? found : !found
  }
  return filter.operator === 'contains' ? str.includes(target.trim()) : str === target.trim()
}

export function applyReportFilters<T>(
  rows: T[],
  filters: FilterRow[],
  columns: ColumnDef[],
  computed?: ComputedFields
): T[] {
  return rows.filter((row) => filters.every((f) => matchesFilter(row, f, columns, computed)))
}
